/**
 * Portal API client. Each function takes an ID token
 * (the Cognito JWT from useAuth().user.idToken) and calls
 * the protected endpoints under /api/portal/*.
 *
 * Throws ApiError with status + code on non-2xx responses.
 * Callers can pattern-match on error.status === 401 to
 * trigger re-authentication.
 */

import type {
  QuoteResult, RateCard, RateCardModifiers,
  // round9/embed-config — per-builder designer configuration (core 5.0.x).
  ToolOptions, CatalogOverrides, CustomBoatBay,
} from "@dockpro/core/types";

// Re-exported so pages/tests import every tenant-shape type from one place.
export type { ToolOptions, CatalogOverrides, CustomBoatBay };

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

export class ApiError extends Error {
  // Note: explicit fields + assignment rather than TS constructor
  // parameter properties — the latter emit runtime code and are
  // disallowed under this project's `erasableSyntaxOnly` tsconfig.
  status: number;
  code: string;
  /**
   * round7/org-admin-panel-ui (P4.6-C): the full parsed error body, when the
   * response had one. OPTIONAL and additive — every pre-existing `new
   * ApiError(status, code, message)` call site and every existing test
   * constructs it exactly as before and gets `body: null`.
   *
   * It exists because two `/api/portal/my-org/*` responses carry decision-
   * relevant fields BESIDE `{code, message}` that a UI must render to be
   * useful rather than merely negative: the 422
   * ENTITLEMENTS_EXCEED_ORG_CEILING body's `candidate`/`orgCeiling` (which
   * field exceeded, and by what), and the 502 CREDENTIAL_EMAIL_FAILED body's
   * `passwordRotated` (the credential really did change even though the email
   * didn't send — an operator MUST be told that). Collapsing those to a
   * message string would throw away the only actionable part.
   *
   * This mirrors what P4.6-B's `AdminApiError.body` already does on the
   * platform-admin plane; the same need arrived on the tenant plane, so the
   * tenant plane's own error type grew the same field rather than a second
   * error class being introduced alongside it.
   */
  body: Record<string, unknown> | null;

  constructor(
    status: number,
    code: string,
    message: string,
    body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// Shared response handling for both authFetch and publicFetch below — the
// exact same ApiError-throwing contract either way; the only difference
// between the two is which headers go out on the request.
async function handleResponse(response: Response): Promise<unknown> {
  if (!response.ok) {
    let errBody: { code?: string; message?: string } = {};
    try { errBody = await response.json(); } catch { /* not JSON */ }
    throw new ApiError(
      response.status,
      errBody.code || "UNKNOWN",
      errBody.message || `Request failed: ${response.status}`,
      // P4.6-C: carry the parsed body through. `errBody` starts as `{}` and
      // stays that way for a non-JSON response, so pass null in that case
      // rather than an empty object a caller would have to tell apart from a
      // real-but-empty body.
      Object.keys(errBody).length > 0 ? errBody as Record<string, unknown> : null,
    );
  }

  if (response.status === 204) return null;
  return response.json();
}

/**
 * round7/org-admin-panel-ui (P4.6-C): exported so `myOrgApi.ts` can build the
 * `/api/portal/my-org/*` calls on the EXACT same primitive every other
 * `/api/portal/*` call already uses — one Authorization-header convention and
 * one ApiError contract for the whole tenant plane.
 *
 * That sharing is the point, and it is why the my-org client is not a
 * self-contained copy the way `adminApi.ts` is: the platform-admin client is
 * separate because it speaks to a DIFFERENT authorizer with a different error
 * posture (an unreadable CORS-less 401 it has to synthesize a code for). The
 * my-org routes are the same authorizer, the same token and the same error
 * envelope as `/api/portal/tenant`, so a second fetch helper here would be
 * two things that must agree, rather than one thing.
 */
export async function authFetch(
  path: string,
  idToken: string,
  init: RequestInit = {},
): Promise<unknown> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      "Authorization": `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
  });
  return handleResponse(response);
}

// round7/signup-ui (P4.5-B) — a sibling to authFetch for the two routes that
// run BEFORE any Cognito session exists: POST /api/register (a visitor
// filling out /signup has no idToken yet — there is no account to attach one
// to) and POST /api/verify-email (its entire purpose is to unblock a FIRST
// login that hasn't happened yet — see DockPro-Backend's
// verifyEmailHandler.ts header). Same ApiError-throwing contract as
// authFetch, minus the Authorization header — deliberately NOT "authFetch
// called with an empty token" (that would send a literal `Authorization:
// Bearer ` header, a real, malformed header some backends/proxies treat
// differently from omitting the header entirely); this is an honest,
// separate sibling for a genuinely different request shape, not a shortcut
// through the authenticated one.
async function publicFetch(
  path: string,
  init: RequestInit = {},
): Promise<unknown> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...init.headers,
      "Content-Type": "application/json",
    },
  });
  return handleResponse(response);
}

// === Types matching backend contract ===

export type LeadStatus = "new" | "acknowledged" | "closed";
export type LeadStage = "new" | "contacted" | "quoted" | "won" | "lost";

export interface LeadSummary {
  leadId: string;
  idempotencyKey: string;
  submissionTimestamp: number;
  homeownerName: string;
  homeownerEmail: string;
  homeownerPhone?: string;
  submissionStatus: LeadStatus;
  stage?: LeadStage;   // Round 18.2 — Kanban column (absent on legacy leads → "new")
  city?: string;       // Round 18.1 location (shown on Kanban card)
  zip?: string;
  // round11/portal-create-lead — "portal" when a builder filed the lead
  // from /leads/new; absent on every homeowner submission.
  source?: "portal";
  receivedAt: string;
  quoteResult?: {
    lowEstimate: number;
    highEstimate: number;
  };
}

export interface LeadDetail extends Omit<LeadSummary, "quoteResult"> {
  // Opaque core-owned blob (this repo never inspects dock geometry directly
  // — see estimate.ts/exports/*). Round 1 f1-schema adds two additive,
  // THIS-REPO-OWNED fields directly on it (mirroring DockPro-Backend's
  // designVersions.ts doc comment): `versions` (metadata-only — see
  // DesignVersionMetadata below; never every version's full nested
  // designState, for payload size) and `currentVersionId`. Neither is
  // modeled in this `unknown` type (reading them is still "inspecting the
  // opaque blob" territory) — use lib/designVersions.ts's helpers instead of
  // reaching into designState directly. A lead submitted before this
  // shipped has neither field at all; the helpers treat that as "no
  // versions yet," not an error.
  designState: unknown;
  builderContext: {
    sourceUrl: string;
    embedVersion: string;
    userAgent?: string;
  };
  clientTimestamp: string;
  // round11/portal-create-lead — who filed a portal-created lead (detail
  // row only; the list summary carries just `source`).
  createdBy?: { sub?: string; email?: string; role?: "owner" | "rep" };
  acknowledgedAt?: number;
  closedAt?: number;
  // The detail endpoint returns the lead row verbatim, so unlike
  // LeadSummary's simplified { lowEstimate, highEstimate } (built by the
  // list handler), this is the real, full @dockpro/core QuoteResult shape.
  // Mirrors whichever version is CURRENT right now — overwritten by every
  // PUT .../versions save (see saveLeadVersion below).
  quoteResult?: QuoteResult;
  // Round 2 f1-write-path: the quote pinned from versions[0] (the original
  // homeowner submission) the FIRST time a lead is saved through
  // saveLeadVersion, and never touched again after that — see that
  // function's doc comment. Absent when versions[0] itself never had a
  // quote (nothing honest to pin) OR the lead predates this endpoint ever
  // being called. Deliberately a SEPARATE field from `quoteResult` above,
  // which tracks the current version instead — the two can diverge once a
  // later version's design/pricing differs from the original.
  homeownerQuote?: QuoteResult;
  // round7/lead-activity-backend: always an array (`[]` when absent/empty —
  // see portalLeadDetailHandler.ts's own doc comment on why these two are
  // normalized to arrays, unlike designState.versions above which is left
  // untouched, absent-or-not, on a pre-migration lead). Neither is ever the
  // full story on its own — see `activityFeed` below.
  notes: LeadNote[];
  stageHistory: StageHistoryEntry[];
  // A single chronological feed composed server-side from the lead's
  // original submission, its designState.versions, its notes, and its
  // stageHistory — see DockPro-Backend's leadActivity.ts composeActivityFeed()
  // doc comment for the exact composition/sort rules (submission pinned
  // first unconditionally; everything else stable-sorted by timestamp
  // ascending, with a version's null createdAt sorting before every known
  // timestamp). This is a read model the backend derives, not separately
  // fetched — render it as given rather than re-deriving/re-sorting it
  // client-side.
  activityFeed: ActivityFeedEntry[];
}

// === Lead notes (round7/lead-activity-backend) ==============================
// Mirrors DockPro-Backend's lib/lambda/utils/validation.ts's LeadNote shape
// exactly (same mirror-don't-import posture as DesignVersionEntry above).
// APPEND-ONLY BY DESIGN: there is deliberately no PUT/PATCH/DELETE route for
// notes (see createLeadNote's own doc comment below) — a correction is a new
// note, not a mutation of an old one. There must never be an edit/delete
// affordance for a note in this UI; that isn't a gap to fill in later.
export type LeadNoteAuthorRole = "owner" | "rep";

export const MAX_NOTE_TEXT_LENGTH = 2000;

export interface LeadNote {
  noteId: string;
  text: string;
  authorUserId: string;
  authorRole: LeadNoteAuthorRole;
  createdAt: string; // ISO 8601 — always known; a note is never migrated from legacy data.
}

// === Stage history (round7/lead-activity-backend) ===========================
// Mirrors DockPro-Backend's validation.ts's StageHistoryEntry. An append-only
// record of every stage change updateLeadStage has ever caused for this
// lead — a lead whose stage moved before this shipped simply has no history
// entries predating it (never backfilled/fabricated).
export interface StageHistoryEntry {
  stage: LeadStage;
  byUserId: string;
  at: string;
}

// === Composed activity feed (round7/lead-activity-backend) =================
// Mirrors DockPro-Backend's leadActivity.ts entry shapes one-for-one. Kept as
// a discriminated union on `type`, exactly like the backend's own
// ActivityFeedEntry, so a renderer can switch on `entry.type` the same way
// the backend's own composer branches on it.
export interface SubmissionFeedEntry {
  type: "submission";
  at: string;
  homeownerName?: string;
  homeownerEmail?: string;
}

// Deliberately NOT DesignVersionMetadata: the backend composes this entry
// from the lead's PRE-projection version list (see leadActivity.ts's doc
// comment — it needs `authoredBy`, which the metadata-only projection below
// strips), so it carries the same fields DesignVersionMetadata does plus
// `authoredBy`, rather than being that type verbatim.
export interface VersionFeedEntry {
  type: "version";
  at: string | null;
  versionId: string;
  authorKind: DesignVersionAuthorKind;
  authoredBy?: string;
  label?: string;
}

export interface NoteFeedEntry {
  type: "note";
  at: string;
  noteId: string;
  text: string;
  authorUserId: string;
  authorRole: LeadNoteAuthorRole;
}

export interface StageChangeFeedEntry {
  type: "stage_change";
  at: string;
  stage: LeadStage;
  byUserId: string;
}

export type ActivityFeedEntry =
  | SubmissionFeedEntry
  | VersionFeedEntry
  | NoteFeedEntry
  | StageChangeFeedEntry;

// === Design versions (Round 1 f1-schema / Round 2 f1-write-path) ===========
// Mirrors DockPro-Backend's lib/lambda/utils/validation.ts /
// designVersions.ts — `versions`/`currentVersionId` live ON the lead's
// designState object itself, not on the enclosing LeadDetail (see its doc
// comment above). Kept here as this repo's own validation-shape mirror
// (same pattern api.ts already uses for TenantPricing/PricingExtensionsConfig)
// rather than importing a core type — core doesn't know about versions at
// all; this is a backend-repo bolt-on additive to the opaque blob core owns.
export type DesignVersionAuthorKind = "homeowner" | "builder";

// The metadata-only shape the lead detail/list endpoints project every
// version down to (portalLeadDetailHandler.ts) — NEVER the nested
// designState or quoteResultSnapshot, to keep that payload small. Fetch one
// version's full content via getLeadVersion() below.
export interface DesignVersionMetadata {
  versionId: string;
  authorKind: DesignVersionAuthorKind;
  // ISO 8601, or null when unknown (a pre-migration version core's
  // migration rung manufactured without a real historical timestamp).
  createdAt: string | null;
  label?: string;
}

// The FULL version entry — returned only by GET .../versions/{versionId}
// (portalLeadVersionHandler.ts), the one place a version's nested
// designState travels.
export interface DesignVersionEntry extends DesignVersionMetadata {
  designState: unknown;
  // userId of the author, when known. Always undefined for a homeowner's
  // own submission — there is no builder-portal user identity to attribute
  // that to.
  authoredBy?: string;
  // This repo's own QuoteResult shape, snapshotted at save time — absent
  // when no quote was computed for this version (quoting disabled, or an
  // entitlement suppressed cost output) for the tenant at that time.
  quoteResultSnapshot?: QuoteResult;
}

// Body for saveLeadVersion (PUT .../versions) below.
export interface SaveLeadVersionInput {
  baseVersionId: string;
  designState: unknown;
  label?: string;
}

// The PUT .../versions success response — NOT the full updated lead (unlike
// updateLeadStatus/updateLeadStage above). It's a small confirmation of what
// was just written; callers that need the refreshed lead (e.g. to re-render
// the version list) should call getLeadDetail again — see
// portalSaveLeadVersionHandler.ts's own response-shape doc comment.
export interface SaveLeadVersionResult {
  leadId: string;
  versionId: string;
  currentVersionId: string;
  authorKind: DesignVersionAuthorKind;
  authoredBy?: string;
  createdAt: string | null;
  label?: string;
  quoteResultSnapshot?: QuoteResult;
}

export interface LeadsListResponse {
  items: LeadSummary[];
  nextCursor: string | null;
}

export interface TenantConfig {
  tenantId: string;
  mode: "builder" | "demo";
  // "pending_card" (2026-09-10) is the card-gated-trial signup state: the
  // account exists but nothing is served until Stripe Checkout collects a
  // card. The owner CAN still reach the portal in this state — that is how
  // they finish checkout — so the Billing page has to render it.
  status: "active" | "trialing" | "past_due" | "unpaid" | "canceled" | "suspended" | "pending_card";
  branding: {
    builderName: string;
    builderEmail: string;
    primaryColor: string;
    accentColor?: string;
    logoUrl?: string;
    productName?: string;
    agencyName?: string;
  };
  quotingEnabled: boolean;
  quoteVisibility?: "homeowner" | "internal";
  disclaimerText?: string;
  rateCard?: RateCard;
  rateCardModifiers?: RateCardModifiers;
  // Backend-only quoting enhancements layered on top of RateCard (NOT part
  // of the published @dockpro/core RateCard type — see DockPro-Backend's
  // lib/pricing/pricingExtensions.ts).
  pricingExtensions?: PricingExtensionsConfig;
  demoConfig?: unknown;
  // Round 23.2 — estimate visibility (gates the cost range in customer PDFs).
  estimate?: { showToCustomer?: boolean };
  // Round 23.3 — per-tenant estimate pricing (builder-authed endpoint only;
  // never surfaced on the public /api/config). Feeds the design estimate.
  pricing?: TenantPricing;
  // Round 1 tier model — derived via @dockpro/core's resolveEntitlements()
  // server-side (see CLAUDE.md's Round 1 rules: never re-derive this
  // locally from a tier name). Round 3: DockPro-Backend's
  // buildPortalTenantResponse now projects the full resolved entitlements
  // onto GET /api/portal/tenant. Every EXISTING reader (LeadDetailPage's
  // costOutputsEnabled gate) still treats "absent" as "not known to be
  // restricted" via its own `?.`/`!== false` checks — untouched by this
  // comment update, still correct regardless of this field's optionality.
  // round7/billing-ui (P4-B): now REQUIRED, not optional — confirmed
  // current backend behavior is to always include it (buildPortalTenantResponse
  // never wraps this in the `!== undefined` conditional-spread pattern it
  // uses for genuinely-optional fields below), and BillingPage.tsx's
  // "active" state needs `submitLimitPerMonth` (NEW field on this type,
  // added alongside costOutputs/editScope/templatesOnly) to describe what a
  // tenant can do without ever naming a tier — see lib/billing.ts's
  // entitlementFacts(). Widening an optional field to required is
  // source-compatible with every existing `tenant.entitlements?.x` call
  // site (optional chaining on a non-optional field still type-checks).
  entitlements: {
    submitLimitPerMonth: number | null;
    costOutputs: boolean;
    editScope: string[] | "full";
    templatesOnly: boolean;
  };
  // round7/billing-ui (P4-B) — resolveBillingContext(tenant, org)'s
  // `billedAt`, always present. "tenant-org": this tenant manages its own
  // billing (today's only real case — the four states BillingPage.tsx
  // builds against `status` below). "parent-org": billing is owned by a
  // parent org (enterprise, V3) — BillingPage.tsx renders a read-only
  // "managed elsewhere" state and skips every other billing UI entirely.
  // Unreachable today (no enterprise org exists yet) but always present in
  // the response shape, checked FIRST before any status-based branching.
  billedAt: "tenant-org" | "parent-org";
  // Unix seconds / boolean — present ONLY once a subscription webhook has
  // actually landed (see DockPro-Backend's stripeWebhookHandler.ts and its
  // DEBT.md's round7/billing-ui-backend entry for the exact mechanics, and
  // the documented "customer.subscription.created isn't handled" gap).
  // Absent for a tenant that's never checked out, or in the window between
  // checkout and the first `.updated` event. BillingPage.tsx treats absence
  // as "unknown" — never as an error, and never as "no renewal."
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
  // round7/signup-ui (P4.5-B) / its P4.5-A backend companion — Unix
  // MILLISECONDS (NOT seconds — a different unit than currentPeriodEnd
  // above, which mirrors Stripe's seconds convention; see DockPro-Backend's
  // registerHandler.ts header, "TRIAL END DATE" section, for the full
  // contract). Present ONLY for a tenant that actually registered through
  // self-signup — absent for every admin-provisioned tenant
  // (scripts/provisionTenant.ts has no concept of a trial at all) and absent
  // for a real paid non-trial tenant. Written ONCE at registration (now + 30
  // days) and NEVER changed afterward by anything in the backend — including
  // after the trial converts (status -> "active") or lapses (status ->
  // "canceled") — so its presence alone, independent of the CURRENT status,
  // is what lets lib/billing.ts's deriveBillingViewState distinguish a
  // lapsed trial (status: "canceled" WITH trialEndsAt) from a tenant with no
  // trial history at all (status: "canceled"/"unpaid"/"suspended" with no
  // trialEndsAt) — see that function's own doc comment.
  trialEndsAt?: number;
  // Round 7 D3 — the tenant's currently-selected "designer preset" (see
  // DesignerPresetName below for the three values and what each means).
  // Absent for a tenant that has never selected one — same "present only
  // when set" posture as pricing/demoConfig/etc. above (see
  // DockPro-Backend's portalTenantResponse.ts doc comment: this is a
  // denormalized "which named preset produced today's entitlementOverrides"
  // field, distinct from — and not reconstructible from — `entitlements`
  // above, which reflects the CURRENTLY RESOLVED, possibly-already-
  // preset-restricted state, not any tier ceiling).
  designerPreset?: DesignerPresetName;
  // round8/engineering-rules — per-tenant structural conventions
  // (v1: pilingSpacingFt only; see docs/planning/DockPro-Engineering-Rules-
  // Plan.md in the sibling DockPro repo for the full design). Raw passthrough
  // from GET /api/portal/tenant: present only once an owner has actually set
  // an override via PUT, absent otherwise — the backend's
  // resolveEngineeringRules() applies the real default (8 ft) server-side
  // when absent, exactly the "present only when set, never defaulted here"
  // posture as demoConfig/designerPreset/pricing above. This repo has no
  // dependency on @dockpro/core's ENGINEERING_RULES registry at runtime, so
  // SettingsPage.tsx hardcodes the bounds/default as UI constants for a
  // courtesy pre-check only — see that file's own doc comment.
  engineeringRules?: { pilingSpacingFt?: number };
  // round9/embed-config — per-builder designer configuration, RAW as stored
  // (present only when set; `null` after a clear — the backend's PUT accepts
  // `null` for toolOptions/catalogOverrides). Resolution to the always-
  // present shapes the embed reads happens server-side on /api/config, never
  // here. `customBoatBays` is APPEND-ONLY server-side: send the stored list
  // plus new entries; removing or resizing a stored one is a 422
  // CUSTOM_BOAT_BAY_IMMUTABLE. See lib/embedConfig.ts for the wire rules.
  toolOptions?: ToolOptions | null;
  catalogOverrides?: CatalogOverrides | null;
  customBoatBays?: CustomBoatBay[];
  createdAt: number;
  updatedAt: number;
}

// Round 7 D3 — a builder-chosen, named configuration of what a SPECIFIC
// embed exposes to homeowners (see DockPro-Backend's @dockpro/core
// `designerPresets.js` header for the full product contract this mirrors
// one-for-one, same "mirror core's shape locally" posture as
// LeadNoteAuthorRole/DesignVersionAuthorKind above — this repo has no
// dependency on @dockpro/core at all, so the three literal values are
// hand-rolled here rather than imported):
//   "full"     — everything: the full editor, free-draw included, with no
//                requirement to start from a template.
//   "guided"   — every structural editor section is available, but a
//                homeowner must START from one of the tenant's templates
//                (no blank-canvas start); they may still edit structurally
//                from there once started.
//   "showcase" — look-only editing (materials/colors/etc.) and every
//                submission must match one of the tenant's approved
//                templates exactly — identical to today's "basic" TIER's
//                shape, now selectable independent of tier.
// A tier still caps which of these a tenant may select (enforced
// server-side via @dockpro/core's `isPresetAllowedForTier` — see
// portalUpdateTenantHandler.ts's 422 PRESET_NOT_ALLOWED_FOR_TIER). This
// repo deliberately does NOT reproduce that check client-side — see
// SettingsPage.tsx's "Embed configuration" section doc comment for why.
export type DesignerPresetName = "full" | "guided" | "showcase";

export interface TenantPricing {
  deckingRatesPerSqft?: { [material: string]: number };
  featureAddons?: {
    pullinSlip?: number; sideTieSlip?: number; boatLift?: number;
    stairStraight?: number; stairL?: number; roof?: number; railingPerFt?: number;
  };
  laborApproach?: "built-in" | "markup-pct" | "hourly" | "fixed-min";
  laborValue?: number;
  marginPct?: number;
}

export interface PricingExtensionsConfig {
  perGangwayLFUSD?: number;
  marginMode?: "onCost" | "onPrice";
  perFloatUSD?: number;
  // $/sqft deck rate by material (@dockpro/core catalog key, e.g. "hardwood",
  // "composite" — see MATERIAL_PRESETS). Priced as a delta adjustment against
  // the flat rateCard deck rate, not a replacement — see DockPro-Backend's
  // lib/pricing/pricingExtensions.ts.
  deckRatesByMaterialUSD?: { [material: string]: number };
}

// === API functions ===

export async function listLeads(
  idToken: string,
  opts: { status?: "active" | "closed"; cursor?: string } = {},
): Promise<LeadsListResponse> {
  const params = new URLSearchParams();
  if (opts.status) params.set("status", opts.status);
  if (opts.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  const path = `/api/portal/leads${qs ? `?${qs}` : ""}`;
  return await authFetch(path, idToken) as LeadsListResponse;
}

export async function getLeadDetail(
  idToken: string,
  idempotencyKey: string,
): Promise<LeadDetail> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}`,
    idToken,
  ) as LeadDetail;
}

export async function getTenant(idToken: string): Promise<TenantConfig> {
  return await authFetch("/api/portal/tenant", idToken) as TenantConfig;
}

// Round 1 f1-schema — fetch one version's FULL content (including its
// nested designState), the one place that travels. Use this both to preview
// an old version (feed its designState into the existing read-only
// `load-design` embed message, same mechanism used for the lead's current
// design) and, right before a restore, to get the exact designState to
// resubmit via saveLeadVersion below.
export async function getLeadVersion(
  idToken: string,
  idempotencyKey: string,
  versionId: string,
): Promise<DesignVersionEntry> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}/versions/${encodeURIComponent(versionId)}`,
    idToken,
  ) as DesignVersionEntry;
}

// Quoting toggle + rate card self-service (the "Rate Card" page in the
// UI, /rate-card). Any subset of these nine keys; each is validated
// independently server-side. rateCard / rateCardModifiers /
// pricingExtensions are whole-object REPLACEs, not a merge — always send
// the full current object with your edits layered on top (SettingsPage
// does this from its initial getTenant() fetch).
//
// `pricing` (Round 23.3's TenantPricing, feeding the design estimate — see
// estimate.ts) is DIFFERENT: the backend does a read-modify-write MERGE on
// it, so unlike the whole-object-replace fields above, sending just the
// changed top-level sub-key(s) (deckingRatesPerSqft / featureAddons /
// laborApproach / laborValue / marginPct) is correct and sufficient —
// no need to fetch-then-send the entire pricing object.
//
// `designerPreset` (Round 7 D3) is a plain single-value field, like
// quotingEnabled/quoteVisibility/disclaimerText above — send one of the
// three DesignerPresetName strings to select it, or omit the key entirely
// to leave the tenant's current selection untouched (there is no "unset"
// sentinel value; SettingsPage mirrors quoteVisibility's own "only include
// when truthy" posture for exactly this reason). Server-side, the backend
// also merges this preset's {editScope, templatesOnly} onto
// entitlementOverrides — see portalUpdateTenantHandler.ts — but that's
// transparent to this client; the response's `designerPreset` is the only
// thing this repo reads back.
export type UpdateTenantPatch = Partial<Pick<TenantConfig,
  "quotingEnabled" | "quoteVisibility" | "disclaimerText" |
  "rateCard" | "rateCardModifiers" | "pricingExtensions" | "pricing" |
  "designerPreset" | "engineeringRules" |
  // round9/embed-config — all three are whole-field replaces (see TenantConfig).
  "toolOptions" | "catalogOverrides" | "customBoatBays">>;

export async function updateTenant(
  idToken: string,
  patch: UpdateTenantPatch,
): Promise<TenantConfig> {
  return await authFetch("/api/portal/tenant", idToken, {
    method: "PUT",
    body: JSON.stringify(patch),
  }) as TenantConfig;
}

export async function updateLeadStatus(
  idToken: string,
  idempotencyKey: string,
  status: LeadStatus,
): Promise<LeadDetail> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}/status`,
    idToken,
    {
      method: "PUT",
      body: JSON.stringify({ status }),
    },
  ) as LeadDetail;
}

// Round 18.2 — move a lead to a new pipeline stage. Returns the updated lead
// (won/lost also flip submissionStatus to "closed" server-side).
export async function updateLeadStage(
  idToken: string,
  idempotencyKey: string,
  stage: LeadStage,
): Promise<LeadDetail> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}/stage`,
    idToken,
    {
      method: "PUT",
      body: JSON.stringify({ stage }),
    },
  ) as LeadDetail;
}

// round7/lead-activity-backend — POST .../notes. Both `owner` and `rep` may
// call this (no role gate — `authorRole` on the returned note just records
// who wrote it, per DockPro-Backend's portalCreateLeadNoteHandler.ts doc
// comment); mirrors saveLeadVersion's "no client-side role gate" posture
// just below. `text` must be non-empty (after trimming) and at most
// MAX_NOTE_TEXT_LENGTH characters — the backend re-validates and 400s with
// code MISSING_NOTE_TEXT or NOTE_TEXT_TOO_LONG otherwise; callers should
// surface `err.message` directly for those two codes (it's already the
// exact, specific copy — see LeadDetailPage's noteErrorMessage) rather than
// falling back to friendlyErrorMessage's generic copy.
//
// Success returns just the new LeadNote (not the full updated lead, same
// "small confirmation, not the whole resource" posture as
// SaveLeadVersionResult below) — callers that need the refreshed lead/feed
// should call getLeadDetail again afterward.
//
// APPEND-ONLY: there is no PUT/PATCH/DELETE route for notes and this file
// must never grow one — see LeadNote's doc comment above.
export async function createLeadNote(
  idToken: string,
  idempotencyKey: string,
  text: string,
): Promise<LeadNote> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}/notes`,
    idToken,
    {
      method: "POST",
      body: JSON.stringify({ text }),
    },
  ) as LeadNote;
}

// Round 2 f1-write-path — save a new design version (both `owner` and `rep`
// may call this; no role gate here). ALSO how a "restore" works: there is no
// separate restore endpoint — the caller re-submits an OLD version's own
// `designState` (verbatim, from a prior getLeadVersion() response) as the
// new save, with `baseVersionId` set to the lead's CURRENT
// `currentVersionId` (optimistic concurrency, enforced server-side via a
// genuine DynamoDB ConditionExpression, not an app-level read-compare-write).
//
// Success does NOT return the full updated lead (see SaveLeadVersionResult's
// own doc comment) — callers should call getLeadDetail again afterward to
// refresh what they render.
//
// A stale baseVersionId (the lead's current version moved since the caller
// last read it) comes back as 409 `{ error: "stale_version" }` — callers
// should check `err.status === 409` (same pattern as the existing
// `err.status === 401` checks elsewhere in this file) rather than string-
// matching the body, and must not silently retry or attempt a client-side
// merge; surface it and let the builder reload.
export async function saveLeadVersion(
  idToken: string,
  idempotencyKey: string,
  body: SaveLeadVersionInput,
): Promise<SaveLeadVersionResult> {
  return await authFetch(
    `/api/portal/leads/${encodeURIComponent(idempotencyKey)}/versions`,
    idToken,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  ) as SaveLeadVersionResult;
}

// === Builder-authored templates (Round 4 D1) ================================
// Mirrors DockPro-Backend's lib/lambda/utils/validation.ts's `TenantTemplate`
// shape exactly: `designState` here is the same PRESET-SHAPED payload
// @dockpro/core's PRESETS catalog / isTemplateDerived() use — NOT the
// versions-wrapped shape a stored Lead's own `designState` has (see
// DesignVersionEntry above). A template is saved from a lead's CURRENT
// design (lead.designState, or a fetched DesignVersionEntry.designState),
// stripped of the lead's versions/currentVersionId wrapper implicitly —
// callers pass whichever designState they're previewing, which is already
// the right shape either way since both are @dockpro/core preset-shaped
// blobs this repo treats as opaque.
export interface TenantTemplate {
  id: string;
  name: string;
  designState: unknown;
  createdAt: string;
}

// GET /api/portal/templates — owner OR rep (read-only). Always an array,
// possibly empty.
export async function listTemplates(idToken: string): Promise<TenantTemplate[]> {
  const result = await authFetch("/api/portal/templates", idToken) as { templates: TenantTemplate[] };
  return result.templates;
}

// POST /api/portal/templates — OWNER ONLY server-side (403 FORBIDDEN for a
// `rep`). This repo has no client-side role/group claim to gate the button
// on ahead of time (see AuthenticatedUser in lib/auth.ts — no role field),
// so callers should catch ApiError and surface `err.code === "FORBIDDEN"`
// with a friendly "owner only" message rather than assuming success.
// Other errors per the backend contract: 400 INVALID_NAME /
// MISSING_DESIGN_STATE / INVALID_DESIGN_STATE, 422 TEMPLATE_LIMIT_REACHED
// (cap = 20/tenant).
// POST /api/portal/leads — round11/portal-create-lead. Owner OR rep. The
// builder-side twin of the homeowner's /api/submit: `contact` follows the
// same rules (name + valid email required; phone/address/city/zip/message
// optional, trimmed server-side), `designState` must be a preset or template
// the server can load (400 INVALID_DESIGN_STATE otherwise), and
// `idempotencyKey` is the caller's — generate ONE per form and reuse it on
// retry; a replay is a 409 carrying the original lead's ids, which callers
// should treat as success. The new lead's URL is /leads/{idempotencyKey}.
// No quota count, no notification (see DockPro-Backend DEBT.md).
export interface CreateLeadBody {
  idempotencyKey: string;
  contact: {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    zip?: string;
    message?: string;
  };
  designState: unknown;
}

export interface CreateLeadResult {
  leadId: string;
  idempotencyKey: string;
  receivedAt: string;
}

export async function createLead(
  idToken: string,
  body: CreateLeadBody,
): Promise<CreateLeadResult> {
  return await authFetch("/api/portal/leads", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  }) as CreateLeadResult;
}

export async function createTemplate(
  idToken: string,
  body: { name: string; designState: unknown },
): Promise<TenantTemplate> {
  return await authFetch("/api/portal/templates", idToken, {
    method: "POST",
    body: JSON.stringify(body),
  }) as TenantTemplate;
}

// DELETE /api/portal/templates/{templateId} — OWNER ONLY server-side, same
// posture as createTemplate above. 404 NOT_FOUND if already gone (e.g.
// deleted from another tab) — callers should treat that as "already
// deleted," not a hard failure, same spirit as other idempotent-delete UIs.
export async function deleteTemplate(
  idToken: string,
  templateId: string,
): Promise<{ deleted: true; id: string }> {
  return await authFetch(
    `/api/portal/templates/${encodeURIComponent(templateId)}`,
    idToken,
    { method: "DELETE" },
  ) as { deleted: true; id: string };
}

// PUT /api/portal/templates/{templateId} — OWNER ONLY server-side, same
// posture as createTemplate/deleteTemplate above (round8/template-authoring).
// PARTIAL update: pass `name` alone to rename, `designState` alone to replace
// the design, or both. At least one is required — an empty patch is a 400
// NO_FIELDS_TO_UPDATE, not a no-op. `id` and `createdAt` are preserved
// server-side; this is an edit, not a replace.
//
// The server NEVER stores the posted designState verbatim — it re-derives a
// canonical preset from it (the same treatment createTemplate applies, for
// the same reason: a template's designState is republished on the PUBLIC
// /api/config, and a portal-posted design can carry lead version history and
// free-text builder labels). So the returned entry's designState is the
// sanitized one, which may not deep-equal what you sent — always trust the
// response over your local copy.
//
// Errors per the backend contract: 400 INVALID_NAME / MISSING_DESIGN_STATE /
// INVALID_DESIGN_STATE / NO_FIELDS_TO_UPDATE, 403 FORBIDDEN (caller is a
// `rep`), 404 NOT_FOUND (template already gone — e.g. deleted in another tab).
export async function updateTemplate(
  idToken: string,
  templateId: string,
  patch: { name?: string; designState?: unknown },
): Promise<TenantTemplate> {
  return await authFetch(
    `/api/portal/templates/${encodeURIComponent(templateId)}`,
    idToken,
    { method: "PUT", body: JSON.stringify(patch) },
  ) as TenantTemplate;
}

// === Usage (round7/billing-ui, P4-B) =========================================
// Mirrors DockPro-Backend's portalUsageHandler.ts response exactly.
// submitLimitPerMonth: null means unlimited, same convention as
// TenantConfig.entitlements.submitLimitPerMonth above (both resolve via the
// same server-side resolveEntitlements(tenant, org) call, so they always
// agree for a given tenant).
export interface UsageResult {
  yearMonth: string; // "YYYY-MM", UTC
  submitCount: number;
  submitLimitPerMonth: number | null;
}

// GET /api/portal/usage — owner OR rep (read-only, informational — see that
// handler's own doc comment for why no owner-only gate applies).
export async function getUsage(idToken: string): Promise<UsageResult> {
  return await authFetch("/api/portal/usage", idToken) as UsageResult;
}

// === Billing (round7/billing-ui, P4-B) =======================================
// Mirrors DockPro-Backend's stripeCheckoutHandler.ts / stripePriceCatalog.ts /
// stripePortalSessionHandler.ts / portalBillingPricesHandler.ts contracts.
// "basic" never appears in the catalog below — it's the default/free tier,
// not something sold through checkout (mirrors stripePriceCatalog.ts's own
// judgment call not to build a speculative basic-tier allowlist ahead of a
// product decision to sell it). No real Stripe account exists yet — see
// DockPro-Backend's DEBT.md; until a human completes the real Stripe setup,
// GET /api/portal/billing/prices returns [] (not an error — BillingPage.tsx
// renders an honest "billing isn't set up yet" message for it) and a
// checkout/portal-session call fails with a STRIPE_NOT_CONFIGURED /
// NO_BILLING_CUSTOMER ApiError.
// "basic" joined the sellable set on 2026-09-10 ($49/mo, $499/yr) — it was
// the unsellable default tier until it got its own Stripe Product and
// Prices. Mirrors the backend's CheckoutTargetTier.
export type BillingTier = "basic" | "mid" | "top";
export type BillingInterval = "monthly" | "annual";

export interface BillingPrice {
  tier: BillingTier;
  interval: BillingInterval;
  priceId: string;
}

// GET /api/portal/billing/prices — owner OR rep (read-only, and NOT
// tenant-scoped — every tenant in an environment sees the same catalog; see
// that handler's own doc comment). The allowlist BillingPage.tsx's
// plan-picker builds its grid from and the ONLY source of valid `priceId`
// values for createCheckoutSession below.
export async function getBillingPrices(idToken: string): Promise<BillingPrice[]> {
  return await authFetch("/api/portal/billing/prices", idToken) as BillingPrice[];
}

export interface CheckoutSessionResult {
  checkoutUrl: string;
}

// round7/billing-ui: BREAKING CONTRACT CHANGE. The backend
// (stripeCheckoutHandler.ts) now takes `{ priceId }` — a real Stripe Price
// id, validated against the GET /api/portal/billing/prices allowlist above
// — NOT the old `{ targetTier: "mid" | "top" }` shape this function sent
// previously. `targetTier`/`CheckoutTargetTier` are gone; callers get a
// `priceId` from a `getBillingPrices()` entry (the tenant's selected
// tier+interval in the plan picker), never construct or guess one.
//
// Owner-only server-side (a "rep" caller gets a 403 ApiError). auth.ts's
// AuthenticatedUser has carried a `role` field since
// round7/d3-scope-config-portal — callers should gate the triggering UI on
// `user.role === "owner"` ahead of time (see BillingPage.tsx, following the
// same client-side-gate-plus-backend-403-backstop pattern SettingsPage.tsx
// already established for its own owner-only actions). This replaces an
// older version of this comment that said the opposite ("this repo doesn't
// know the caller's role client-side") — that was true before the role
// claim was decoded, and had gone stale.
export async function createCheckoutSession(
  idToken: string,
  priceId: string,
): Promise<CheckoutSessionResult> {
  return await authFetch("/api/portal/billing/checkout-session", idToken, {
    method: "POST",
    body: JSON.stringify({ priceId }),
  }) as CheckoutSessionResult;
}

export interface PortalSessionResult {
  portalUrl: string;
}

// POST /api/portal/billing/portal-session — owner-only server-side, no
// request body. Redirect target for "Manage billing" — the entire
// manage-card / view-invoices / change-plan / cancel-subscription surface
// for v1 (Stripe's own hosted portal; see that handler's doc comment). 404
// NO_BILLING_CUSTOMER if the org has never completed a checkout yet —
// BillingPage.tsx never offers this action in its no-subscription state (the
// only state where that could happen), so this is expected to be
// unreachable in practice, not a case with its own bespoke UI copy.
export async function createPortalSession(idToken: string): Promise<PortalSessionResult> {
  return await authFetch("/api/portal/billing/portal-session", idToken, {
    method: "POST",
  }) as PortalSessionResult;
}

// === Self-registration (round7/signup-ui, P4.5-B) ============================
// Mirrors DockPro-Backend's registerHandler.ts / verifyEmailHandler.ts
// contracts exactly. Both routes are PUBLIC/PRE-AUTH — see publicFetch above
// — a not-yet-registered visitor has no idToken, and a not-yet-verified user
// cannot complete a login at all (Cognito's PreAuthentication trigger,
// preAuthEmailVerifiedGate.ts, blocks it with UserLambdaValidationException)
// until POST /api/verify-email succeeds.
//
// tier/interval below deliberately REUSE BillingTier/BillingInterval
// (defined above, for the authenticated checkout flow) rather than a
// separate registration-only enum: registerHandler.ts validates against the
// EXACT SAME mid|top x monthly|annual universe stripeCheckoutHandler.ts's
// price allowlist does (see that handler's header) — "basic" has never been
// sellable anywhere in this system (checkout, billing UI, or now
// registration), so there is no reason for this repo to invent a second,
// parallel type for the same two closed unions.

export interface RegisterConsent {
  policyVersion: string;
  textShown: string;
}

export interface RegisterRequest {
  businessName: string;
  contactName: string;
  email: string;
  tier: BillingTier;
  interval: BillingInterval;
  consent: RegisterConsent;
}

export interface RegisterResult {
  tenantId: string;
  // The account is created INERT (card-gated trial, 2026-09-10) — core's
  // tenantHasAccess denies "pending_card", so /api/config refuses to serve
  // it — and stays that way until the card is submitted.
  status: "pending_card";
  // The Stripe Checkout URL the caller MUST send the user to. A client
  // that ignores this strands the account it just created: there is no
  // other way to activate it.
  checkoutUrl: string;
}

// POST /api/register. The 409 EMAIL_ALREADY_REGISTERED case is a deliberate,
// narrow exception to Cognito's general "never reveal whether an account
// exists" posture (see registerHandler.ts's own header for why) — its
// message is already specific, safe, actionable copy ("...Try logging in, or
// use \"Forgot password\" to regain access.") meant to be shown VERBATIM,
// the same "reuse the backend's own message for a known code" convention
// SettingsPage.tsx's designerPreset error handling already established.
export async function register(body: RegisterRequest): Promise<RegisterResult> {
  return await publicFetch("/api/register", {
    method: "POST",
    body: JSON.stringify(body),
  }) as RegisterResult;
}

// POST /api/verify-email's success shape is EITHER `verified: true` (a fresh
// confirmation) OR `alreadyVerified: true` (a repeat click on the same
// link, e.g. opened in two tabs — see verifyEmailHandler.ts's own doc
// comment on why this is deliberately idempotent, not an error) — a caller
// that just wants to know "did this succeed" can treat both as equivalent
// success.
export interface VerifyEmailResult {
  verified?: true;
  alreadyVerified?: true;
}

// POST /api/verify-email. Known ApiError codes: 400 MISSING_TENANT_ID /
// MISSING_TOKEN / INVALID_TOKEN / NO_PENDING_VERIFICATION / EXPIRED_TOKEN,
// 404 NOT_FOUND, 500 INTERNAL_ERROR — see VerifyEmailPage.tsx's own mapping
// of the ones with dedicated copy.
export async function verifyEmail(tenantId: string, token: string): Promise<VerifyEmailResult> {
  return await publicFetch("/api/verify-email", {
    method: "POST",
    body: JSON.stringify({ tenantId, token }),
  }) as VerifyEmailResult;
}
